import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { Box, CircularProgress, IconButton, MenuItem, TextField, Typography } from '@mui/material'
import ApplicationReview, { useApplicationReview } from '../components/PartnerSignups/ApplicationReview'
import DeleteSignupDialog from '../components/PartnerSignups/DeleteSignup'
import { FormFrame, useApplicationFrame } from '../components/PartnerSignups/ApplicationFrame'
import { STAFF_LINK, openInWizard } from '../components/PartnerSignups/openInWizard'
import { GET_PARTNER_SIGNUP, UPDATE_STATUS, formatDate } from '../components/PartnerSignups/queries'
import { Icon, T } from '../components/PartnerSignups/onboardingDesign'
import {
  Card, CardTitle, FieldLabel, GhostButton, KV, LinkButton, Notice, OnboardingPage, PageHeader, PrimaryButton, StatusBadge, statusLabel,
} from '../components/PartnerSignups/onboardingUi'

/**
 * One partner signup, on its own page. An application shows the merchant
 * sign-up form itself (app.rushmyfood.com, framed, opened with a staff link:
 * the same form, look and saving as the restaurant's), editable while it is a
 * draft and locked once signed, next to the admin side: approve & onboard,
 * fees, agreement, documents, menu, status and notes. "new" starts one.
 * A signup from the old marketing form has no application: its details show
 * instead of the form.
 */

const STATUSES = ['NEW', 'CONTACTED', 'ONBOARDED', 'REJECTED']
// Applications (with a signed agreement) move through DRAFT → SIGNED →
// ONBOARDED on their own; by hand they can only be rejected.
const statusesFor = (r) => (r.isApplication ? [...new Set([r.status, 'REJECTED'])] : STATUSES)

const PAGE = '/super_admin/partner-application'
const LIST = '/super_admin/partner-signups'

// Two columns (form + panel) from this width; the form keeps its desktop
// layout down to 600px inside the frame.
const WIDE = '@media (min-width: 1280px)'
const PANE_HEIGHT = 'calc(100vh - 168px)'


/**
 * Status and internal notes (the old marketing form's rows use these most),
 * and deleting one never signed or onboarded.
 */
function ManageCard({ row, onSaved, onDeleted }) {
  const [deleting, setDeleting] = useState(false)
  const [status, setStatus] = useState(row.status || 'NEW')
  const [notes, setNotes] = useState(row.notes || '')
  const [updateStatus, { loading: saving }] = useMutation(UPDATE_STATUS)
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(false)
  const save = async () => {
    setError(null)
    setSaved(false)
    try {
      await updateStatus({ variables: { id: row._id, status, notes } })
      setSaved(true)
      if (onSaved) onSaved()
    } catch (e) {
      setError(e.message)
    }
  }
  return (
    <Card>
      <CardTitle>Status &amp; notes</CardTitle>
      {error && <Notice severity="error">{error}</Notice>}
      <FieldLabel htmlFor="signup-status" label="Status" />
      <TextField id="signup-status" select fullWidth size="small" value={status} onChange={e => setStatus(e.target.value)} sx={{ mb: 2 }}>
        {statusesFor(row).map(s => <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>)}
      </TextField>
      <FieldLabel htmlFor="signup-notes" label="Notes" optional />
      <TextField id="signup-notes" fullWidth size="small" multiline minRows={2} value={notes} onChange={e => setNotes(e.target.value)} sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <PrimaryButton busy={saving} onClick={save}>Save</PrimaryButton>
        {saved && <Typography sx={{ color: T.green, fontSize: 14, display: 'flex', alignItems: 'center', gap: 0.5 }}><Icon.Check size={16} />Saved</Typography>}
        {row.canDelete && (
          <LinkButton onClick={() => setDeleting(true)} sx={{ ml: 'auto' }}>
            <Icon.Trash size={15} />
            Delete signup
          </LinkButton>
        )}
      </Box>
      <DeleteSignupDialog row={deleting ? row : null} onClose={() => setDeleting(false)} onDeleted={onDeleted} />
    </Card>
  )
}

/** A signup from the old marketing form: what they sent. */
function LegacyDetails({ row }) {
  const fmtAddr = (...parts) => parts.filter(Boolean).join(', ') || '—'
  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Card>
        <CardTitle sub="Sent with the earlier sign-up form, before the full application.">Restaurant</CardTitle>
        <KV rows={[
          ['Restaurant name', row.restaurantName],
          ['DBA address', fmtAddr(row.dbaAddressLine1, row.dbaAddressLine2, row.dbaCity, row.dbaState)],
          ['Restaurant phone', row.restaurantPhone],
          ['Restaurant email', row.restaurantEmail],
          ['Website', row.website],
        ]} />
      </Card>
      <Card>
        <CardTitle>Contact</CardTitle>
        <KV rows={[
          ['Contact name', row.contactName],
          ['Contact email', row.contactEmail],
          ['Contact phone', row.contactPhone],
        ]} />
      </Card>
      <Card>
        <CardTitle>Corporate</CardTitle>
        <KV rows={[
          ['Corporate name', row.corporateName],
          ['Corporate address', fmtAddr(row.corpAddressLine1, row.corpAddressLine2, row.corpCity, row.corpState, row.corpZip)],
          ['Tax ID', row.taxId],
        ]} />
      </Card>
      <Card>
        <CardTitle>Current setup</CardTitle>
        <KV rows={[
          ['Has online ordering', row.hasOnlineOrdering],
          ['DoorDash rate', row.doordashRate],
          ['UberEats rate', row.ubereatsRate],
          ['Grubhub rate', row.grubhubRate],
          ['Agreed to terms', row.agreedToTerms ? 'Yes' : 'No'],
          ['Interested in free POS', row.interestedInPos ? 'Yes' : 'No'],
        ]} />
      </Card>
    </Box>
  )
}

export default function PartnerApplication() {
  const { id } = useParams()
  const history = useHistory()
  const isNew = id === 'new'
  // Bumped when the form saves, so the panel reloads.
  const [version, setVersion] = useState(0)
  const [getTabLink, { loading: openingTab }] = useMutation(STAFF_LINK)
  const [tabError, setTabError] = useState(null)
  // An application created in this frame keeps its frame (no reload).
  const createdHere = useRef(null)

  const { data, loading, error, refetch } = useQuery(GET_PARTNER_SIGNUP, { variables: { id }, skip: isNew, fetchPolicy: 'network-only' })
  // Just created in the frame: a placeholder until its row loads, so the
  // page (and the frame, mid-save) never unmounts.
  const row = (data && data.partnerSignup)
    || (!isNew && createdHere.current === String(id) ? { _id: id, isApplication: true, status: 'DRAFT', restaurantName: 'New application' } : null)
  const status = row && row.status

  // The form: a staff link to this application, or one to start a new
  // application. Asked for as the page opens, not once the row is in: every
  // step of loading crosses to the database. view: a super admin may open any
  // status, and the form itself shows a signed or rejected one locked. It
  // saved: reload the panel. It created the application: show its page,
  // keeping the frame.
  const frame = useApplicationFrame({
    id,
    enabled: isNew || !row || !!row.isApplication,
    view: !isNew,
    created: createdHere,
    onSaved: () => {
      setVersion(v => v + 1)
      Promise.resolve().then(() => refetch()).catch(() => {})
    },
    onCreated: (newId) => history.replace(`${PAGE}/${newId}`),
  })
  // The panel beside the form, also started as the page opens.
  const review = useApplicationReview(id, { skip: isNew })

  // Status changed while open (rejected here, say): the form reloads its open
  // step, which locks it.
  const shownStatus = useRef(null)
  const { refreshForm } = frame
  useEffect(() => {
    if (!status) return
    if (shownStatus.current && shownStatus.current !== status) refreshForm()
    shownStatus.current = status
  }, [status, refreshForm])

  const openTab = () => {
    setTabError(null)
    openInWizard(getTabLink, id).catch(e => setTabError(e.message))
  }

  const back = { label: 'Partner Signups', onClick: () => history.push(LIST) }

  if (!isNew && loading && !row) {
    return <OnboardingPage><Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: T.red }} /></Box></OnboardingPage>
  }
  if (!isNew && (error || !row)) {
    return (
      <OnboardingPage>
        <PageHeader back={back} title="Signup not found" />
        <Notice severity="error">{error ? error.message : 'This signup does not exist.'}</Notice>
      </OnboardingPage>
    )
  }

  const meta = isNew ? null : [
    row.businessType === 'GROCERY' ? 'Grocery' : null,
    row.startedByName ? `Started by ${row.startedByName}` : null,
    row.agentName && row.agentName !== row.startedByName ? `Agent: ${row.agentName}` : null,
    `Submitted ${formatDate(row.createdAt)}`,
    row.signedAt ? `Signed ${formatDate(row.signedAt)}` : null,
  ].filter(Boolean).join(' · ')

  const header = (
    <PageHeader
      back={back}
      title={isNew ? 'New application' : row.restaurantName || 'Untitled'}
      actions={!isNew && row.isApplication ? (
        <>
          {status === 'DRAFT' && (
            <GhostButton onClick={openTab} disabled={openingTab}>
              <Icon.External size={16} />
              Open in a new tab
            </GhostButton>
          )}
          <IconButton aria-label="Reload" onClick={() => { refetch(); setVersion(v => v + 1) }} sx={{ width: 40, height: 40, border: `1px solid ${T.input}`, color: T.muted }}>
            <Icon.Refresh size={18} />
          </IconButton>
        </>
      ) : null}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
        {isNew
          ? <Typography sx={{ color: T.text2, fontSize: 14.5 }}>Fill in step 1 and create the application; then every step can be filled in, and the last step sends it to the restaurant to sign.</Typography>
          : <><StatusBadge status={status} /><Typography sx={{ color: T.hint, fontSize: 14 }}>{meta}</Typography></>}
      </Box>
    </PageHeader>
  )

  // The old marketing form: details and status, no application.
  if (!isNew && !row.isApplication) {
    return (
      <OnboardingPage maxWidth={1100}>
        {header}
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 340px' }, alignItems: 'start' }}>
          <LegacyDetails row={row} />
          <ManageCard key={row._id} row={row} onSaved={() => refetch()} onDeleted={() => history.push(LIST)} />
        </Box>
      </OnboardingPage>
    )
  }

  return (
    <OnboardingPage maxWidth={1680} sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 3, sm: 3 } }}>
      {header}
      {tabError && <Notice severity="error" onClose={() => setTabError(null)}>{tabError}</Notice>}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'minmax(0, 1fr)', [WIDE]: { gridTemplateColumns: 'minmax(0, 1fr) 340px' }, alignItems: 'start' }}>
        <FormFrame frame={frame} sx={{ height: '78vh', [WIDE]: { height: PANE_HEIGHT } }} />
        <Box sx={{ minWidth: 0, [WIDE]: { height: PANE_HEIGHT, overflowY: 'auto', overflowX: 'hidden', pr: 0.5 } }}>
          {isNew ? (
            <Card>
              <CardTitle sub="Once step 1 is saved, the fees, documents and the rest of the application show here.">Starting an application</CardTitle>
              <Typography sx={{ color: T.text2, fontSize: 14 }}>
                Nothing is sent to the restaurant until you send it for signature on the form’s last step.
              </Typography>
            </Card>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 2 }}>
              <ApplicationReview id={id} query={review} version={version} onChanged={() => refetch()} onFeesChanged={frame.refreshForm} />
              <ManageCard key={`${row._id}-${status}`} row={row} onSaved={() => refetch()} onDeleted={() => history.push(LIST)} />
            </Box>
          )}
        </Box>
      </Box>
    </OnboardingPage>
  )
}
